import {
  changePassword,
  fetchCertainUserRequest,
  logInRequest,
  refreshTokenRequest,
  restorePasswordRequest,
  signUpRequest,
} from 'api';
import { resendConfirmationCode, sendConfirmationCode } from 'api/authApi/authApi';
import { NewPasswordRequestType, SignUpRequestType } from 'api/authApi/types';
import { LsKeys, Roles } from 'enums';
import {
  setAuthLoading,
  setLogIn,
  setLogOut,
  setNotification,
  setUserInfo,
  setRecoveryToken,
  setTenant,
} from 'state/actions';
import { AppRootStateType } from 'state/reducers';
import { TypedDispatch } from 'state/types';
import { restoreState, saveState } from 'utils';

export const logIn =
  (email: string, password: string) => async (dispatch: TypedDispatch) => {
    try {
      dispatch(setAuthLoading(true));

      const response = await logInRequest(email, password);

      const payload = {
        accessToken: response?.data.accessToken,
        refreshToken: response?.data.refreshToken,
      };

      if (response) {
        saveState(LsKeys.USER_INFO, response.data.user);
        saveState(LsKeys.TOKEN_INFO, payload);

        dispatch(setLogIn(payload));
        dispatch(setUserInfo(response.data.user));
        dispatch(
          setTenant(
            response.data.user.role === Roles.SUPER_ADMIN
              ? 'admin'
              : response.data.user.company.id,
          ),
        );
      }
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const fetchCurrentUser =
  () => async (dispatch: TypedDispatch, getState: () => AppRootStateType) => {
    try {
      dispatch(setAuthLoading(true));
      const response = await fetchCertainUserRequest(getState().auth.user.id);

      if (response) {
        saveState(LsKeys.USER_INFO, response?.data);
        dispatch(setUserInfo(response?.data));
      }
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const logOut = () => (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));
    localStorage.removeItem(LsKeys.TOKEN_INFO);
    localStorage.removeItem(LsKeys.USER_INFO);
    dispatch(setLogOut());
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const signUp = (params: SignUpRequestType) => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));
    const response = await signUpRequest(params);

    return response?.status;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const restorePassword = (email: string) => async (dispatch: TypedDispatch) => {
  try {
    dispatch(setAuthLoading(true));
    const response = await restorePasswordRequest(email);

    if (response?.data.status) {
      dispatch(setRecoveryToken(response?.data.payload));
      dispatch(setNotification(response?.data.message));
    }

    return response?.status;
  } finally {
    dispatch(setAuthLoading(false));
  }
};

export const sendCode =
  (code: string) => async (dispatch: TypedDispatch, getState: () => AppRootStateType) => {
    const token = getState().auth.recoveryToken;

    try {
      dispatch(setAuthLoading(true));
      const response = await sendConfirmationCode(token, code);

      if (response?.data.status) dispatch(setNotification(response?.data.message));

      return response?.status;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const resendCode =
  () => async (dispatch: TypedDispatch, getState: () => AppRootStateType) => {
    const token = getState().auth.recoveryToken;

    try {
      dispatch(setAuthLoading(true));
      const response = await resendConfirmationCode(token);

      if (response?.data.status) dispatch(setNotification(response?.data.message));
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const sendNewPassword =
  (params: Omit<NewPasswordRequestType, 'token'>) =>
  async (dispatch: TypedDispatch, getState: () => AppRootStateType) => {
    const token = getState().auth.recoveryToken;

    try {
      dispatch(setAuthLoading(true));
      const response = await changePassword({ token, ...params });

      if (response?.data.status) dispatch(setNotification(response?.data.message));

      return response?.status;
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

export const refreshToken = () => async (dispatch: TypedDispatch) => {
  const tokenInfo = restoreState(LsKeys.TOKEN_INFO);

  if (tokenInfo?.refreshToken) {
    try {
      dispatch(setAuthLoading(true));

      const response = await refreshTokenRequest(tokenInfo.refreshToken);

      const payload = {
        accessToken: response?.data.accessToken,
        refreshToken: tokenInfo.refreshToken,
      };

      if (response) {
        saveState(LsKeys.TOKEN_INFO, payload);

        dispatch(setLogIn(payload));

        return response?.data?.accessToken;
      }
    } finally {
      dispatch(setAuthLoading(false));
    }
  }
};
